import React, { useState, useEffect, useRef } from 'react';
import { updateAerodromeByIcao, getThrData, updateThr } from '../api/client';
import { T } from '../assets/theme';

const FEET_TO_M = 0.3048;

/** Decimal degrees -> separate {deg, min, sec, hemi} parts for boxed DMS entry. */
function decimalToDmsParts(dd, isLat) {
    if (dd === null || dd === undefined || dd === '' || Number.isNaN(Number(dd))) {
        return { deg: '', min: '', sec: '', hemi: isLat ? 'N' : 'E' };
    }
    const value = Number(dd);
    const hemi = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const deg = Math.floor(absValue);
    const minutesFull = (absValue - deg) * 60;
    const min = Math.floor(minutesFull);
    const sec = Math.round((minutesFull - min) * 60 * 10000) / 10000;
    return { deg, min, sec, hemi };
}

/** {deg, min, sec, hemi} parts -> signed decimal degrees. */
function dmsPartsToDecimal({ deg, min, sec, hemi }) {
    const d = parseFloat(deg) || 0;
    const m = parseFloat(min) || 0;
    const s = parseFloat(sec) || 0;
    let dd = d + m / 60 + s / 3600;
    if (hemi === 'S' || hemi === 'W') dd = -dd;
    return dd;
}

function Btn({ children, onClick, variant = 'primary', small, disabled, style: sx = {} }) {
    const variants = {
        primary: { bg: T.blue, color: T.white },
        ghost: { bg: 'rgba(58,138,196,0.09)', color: T.blue },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button onClick={onClick} disabled={disabled} style={{
            background: v.bg, color: v.color, border: `1px solid ${v.color}33`,
            borderRadius: 4, padding: small ? '4px 9px' : '7px 14px',
            fontSize: small ? 10 : 11, fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, transition: 'all 0.15s', ...sx,
        }}>{children}</button>
    );
}

const dmsInputStyle = {
    background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 4,
    padding: '7px 7px', color: T.white, fontSize: 11, width: '100%', boxSizing: 'border-box',
};

// Degrees/minutes/seconds as three separate number boxes plus a hemisphere
// dropdown, instead of one free-text DMS string — avoids the user having to
// type the °/'/" symbols by hand. Reused for the ARP and every threshold.
function DmsFieldGroup({ label, value, isLat, onChange, onPickMap }) {
    const [parts, setParts] = useState(() => decimalToDmsParts(value, isLat));
    // Tracks the decimal value *this component itself last emitted*, so the
    // effect below can tell "value changed because the parent echoed our own
    // edit back down" apart from "value changed for an external reason (Pick
    // on Map, switching aerodrome/threshold)". Without this, every keystroke
    // triggers deg/min/sec -> decimal -> deg/min/sec round-trip that fights
    // the user mid-edit (e.g. typing "14" in Minutes gets clobbered by a
    // re-derived value on the very next render).
    const lastEmitted = useRef(value);

    useEffect(() => {
        if (value === lastEmitted.current) return;
        setParts(decimalToDmsParts(value, isLat));
        lastEmitted.current = value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const update = (key, v) => {
        const next = { ...parts, [key]: v };
        setParts(next);
        const dd = dmsPartsToDecimal(next);
        lastEmitted.current = dd;
        onChange(dd);
    };

    const hemiOptions = isLat ? ['N', 'S'] : ['E', 'W'];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase' }}>{label}</div>
                {onPickMap && (
                    <button onClick={onPickMap} style={{ background: 'none', border: 'none', color: T.blue, fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>
                        📍 Pick on Map
                    </button>
                )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.3fr 54px', gap: 4 }}>
                <input type="number" value={parts.deg} onChange={e => update('deg', e.target.value)} placeholder="Deg" style={dmsInputStyle} />
                <input type="number" value={parts.min} onChange={e => update('min', e.target.value)} placeholder="Min" min={0} max={59} style={dmsInputStyle} />
                <input type="number" value={parts.sec} onChange={e => update('sec', e.target.value)} placeholder="Sec" min={0} max={59.9999} step="0.0001" style={dmsInputStyle} />
                <select value={parts.hemi} onChange={e => update('hemi', e.target.value)} style={{ ...dmsInputStyle, padding: '7px 2px', textAlign: 'center' }}>
                    {hemiOptions.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
            </div>
        </div>
    );
}

// Shared aerodrome-edit dialog — writes to arp_table via ICAO (reference-data-
// wide: affects every project using this aerodrome) and, now, to each of the
// aerodrome's thr_table rows too, since a threshold is a child of the
// aerodrome and belongs in the same editor.
// Different pages expose the aerodrome's elevation differently — OlsAdgPage's
// own tree already has elev_m, while the shared `arps` prop (everywhere
// else) only has elev_ft (the raw arp_table.adelev value). Prefer elev_m.
function elevToMeters(aerodrome) {
    if (aerodrome.elev_m != null) return Number(aerodrome.elev_m.toFixed(2));
    if (aerodrome.elev_ft != null) return Number((aerodrome.elev_ft * FEET_TO_M).toFixed(2));
    return '';
}

export default function EditAerodromeModal({ aerodrome, onClose, onUpdated, hidden }) {
    const [form, setForm] = useState({
        elevation_m: elevToMeters(aerodrome),
        lat: aerodrome.lat ?? null,
        lon: aerodrome.lon ?? null,
    });
    const [thresholds, setThresholds] = useState([]);
    const [thrForm, setThrForm] = useState({}); // ogc_fid -> { elevation_m, lat, lon }
    const [thrLoading, setThrLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const setThr = (ogcFid, k, v) => setThrForm(p => ({ ...p, [ogcFid]: { ...p[ogcFid], [k]: v } }));

    useEffect(() => {
        setForm({
            elevation_m: elevToMeters(aerodrome),
            lat: aerodrome.lat ?? null,
            lon: aerodrome.lon ?? null,
        });
    }, [aerodrome]);

    // Load this aerodrome's thresholds — children of the ARP, editable in the
    // same dialog. search matches arptid via ILIKE; ICAO codes are unique
    // enough that this can't cross-match another aerodrome.
    useEffect(() => {
        let cancelled = false;
        setThrLoading(true);
        getThrData(aerodrome.icao, 1).then(({ data }) => {
            if (cancelled) return;
            const rows = data?.rows || [];
            setThresholds(rows);
            const initial = {};
            rows.forEach(r => {
                initial[r.ogc_fid] = {
                    elevation_m: r.elev_m != null ? Number(Number(r.elev_m).toFixed(2))
                        : (r.elev_ft != null ? Number((r.elev_ft * FEET_TO_M).toFixed(2)) : ''),
                    lat: r.thr_lat ?? null,
                    lon: r.thr_lon ?? null,
                };
            });
            setThrForm(initial);
        }).catch(() => { if (!cancelled) setThresholds([]); })
          .finally(() => { if (!cancelled) setThrLoading(false); });
        return () => { cancelled = true; };
    }, [aerodrome.icao]);

    async function submit() {
        setLoading(true);
        setErr('');
        try {
            const payload = {
                adelev: form.elevation_m !== '' ? Math.round(Number(form.elevation_m) / FEET_TO_M) : null,
                arp_lat: form.lat != null ? String(form.lat) : null,
                arp_lon: form.lon != null ? String(form.lon) : null,
            };
            const { data } = await updateAerodromeByIcao(aerodrome.icao, payload);
            if (!data?.aerodrome) throw new Error(data?.error || 'Update failed');

            // Push through only the thresholds that have a form entry (i.e.
            // loaded successfully) — each is independent of the ARP save.
            const thrErrors = [];
            for (const t of thresholds) {
                const tf = thrForm[t.ogc_fid];
                if (!tf) continue;
                try {
                    await updateThr(t.ogc_fid, {
                        thr_lat: tf.lat,
                        thr_lon: tf.lon,
                        elev_m: tf.elevation_m !== '' ? Number(tf.elevation_m) : null,
                        elev_ft: tf.elevation_m !== '' ? Math.round(Number(tf.elevation_m) / FEET_TO_M * 10) / 10 : null,
                    });
                } catch (e) {
                    thrErrors.push(t.threshold || t.thrid || t.ogc_fid);
                }
            }

            // arp_lat/arp_lon come back as fresh decimal degrees (the
            // backend parses whatever DMS/decimal string was sent) — use
            // those, not the raw form state, so the map marker still gets a
            // numeric lat/lon.
            onUpdated({
                ...aerodrome,
                elev_ft: data.aerodrome.adelev,
                lat: data.aerodrome.arp_lat,
                lon: data.aerodrome.arp_lon,
            });
            if (thrErrors.length) {
                setErr(`Aerodrome saved, but these thresholds failed to update: ${thrErrors.join(', ')}`);
                setLoading(false);
                return;
            }
            onClose();
        } catch (err) {
            setErr(err.response?.data?.detail || err.message || 'Failed to update aerodrome.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: hidden ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 8, padding: 20, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>Edit Aerodrome</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 14, cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginBottom: 14 }}>
                    {aerodrome.icao} — {aerodrome.name}
                </div>
                <div style={{ fontSize: 10, color: T.warn || '#f5a623', background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 4, padding: '6px 8px', marginBottom: 12 }}>
                    This is shared reference data — changes apply to every project using {aerodrome.icao}.
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: T.white, marginBottom: 7 }}>ARP Data</div>
                <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>Elevation (m AMSL)</div>
                        <input type="number" value={form.elevation_m} onChange={e => set('elevation_m', e.target.value)} style={{ background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 4, padding: '9px 10px', color: T.white, fontSize: 11, width: '100%', boxSizing: 'border-box' }} />
                    </div>
                    <DmsFieldGroup label="Latitude" value={form.lat} isLat onChange={v => set('lat', v)} />
                    <DmsFieldGroup label="Longitude" value={form.lon} isLat={false} onChange={v => set('lon', v)} />
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, color: T.white, marginBottom: 7, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
                    THR Data
                </div>
                {thrLoading ? (
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>Loading thresholds…</div>
                ) : thresholds.length === 0 ? (
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 10 }}>No thresholds stored for {aerodrome.icao}.</div>
                ) : (
                    <div style={{ display: 'grid', gap: 12, marginBottom: 7 }}>
                        {thresholds.map((t, i) => {
                            const tf = thrForm[t.ogc_fid] || { elevation_m: '', lat: null, lon: null };
                            return (
                                <div key={t.ogc_fid} style={{ background: T.bg0, border: `1px solid ${T.border}`, borderRadius: 5, padding: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 7 }}>
                                        THR {t.threshold || t.thrid || i + 1}
                                    </div>
                                    <div style={{ display: 'grid', gap: 9 }}>
                                        <div>
                                            <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', marginBottom: 3 }}>Elevation (m AMSL)</div>
                                            <input type="number" value={tf.elevation_m} onChange={e => setThr(t.ogc_fid, 'elevation_m', e.target.value)} style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 4, padding: '8px 9px', color: T.white, fontSize: 11, width: '100%', boxSizing: 'border-box' }} />
                                        </div>
                                        <DmsFieldGroup label="Latitude" value={tf.lat} isLat onChange={v => setThr(t.ogc_fid, 'lat', v)} />
                                        <DmsFieldGroup label="Longitude" value={tf.lon} isLat={false} onChange={v => setThr(t.ogc_fid, 'lon', v)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {err && <div style={{ color: '#e84040', fontSize: 11, marginTop: 7 }}>{err}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginTop: 9 }}>
                    <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={submit} disabled={loading}>{loading ? 'Saving…' : 'Save Changes'}</Btn>
                </div>
            </div>
        </div>
    );
}
